import * as React from "react";
import { cn } from "../../lib/utils";
import type { CheckboxProps } from "./checkbox.types";

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      checked,
      indeterminate,
      disabled,
      label,
      description,
      id,
      onChange,
      ...props
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const generatedId = React.useId();
    const inputId = id || generatedId;

    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = Boolean(indeterminate);
      }
    }, [indeterminate]);

    React.useImperativeHandle(ref, () => inputRef.current!);

    const checkboxElement = (
      <span className="relative inline-flex items-center justify-center shrink-0">
        <input
          ref={inputRef}
          id={inputId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          className="peer sr-only"
          {...props}
        />
        <span
          className={cn(
            "flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border border-[#D0D5DD] bg-white transition-all cursor-pointer",
            "peer-hover:border-[#0D41E1] peer-hover:shadow-xs",
            "peer-focus-visible:ring-2 peer-focus-visible:ring-[#0D41E1]/30 peer-focus-visible:ring-offset-1",
            "peer-checked:border-[#0D41E1] peer-checked:bg-[#0D41E1] peer-checked:text-white",
            indeterminate && "border-[#0D41E1] bg-[#0D41E1] text-white",
            disabled &&
              "cursor-not-allowed border-[#EAECF0] bg-[#F2F4F7] opacity-60 peer-checked:bg-[#98A2B3] peer-checked:border-[#98A2B3]",
            className,
          )}
          aria-hidden="true"
        >
          {indeterminate ? (
            <svg
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-2.5 w-2.5 stroke-white"
            >
              <line
                x1="2.5"
                y1="7"
                x2="11.5"
                y2="7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <svg
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={cn(
                "h-2.5 w-2.5 stroke-white transition-transform duration-100",
                checked ? "scale-100 opacity-100" : "scale-75 opacity-0",
              )}
            >
              <path
                d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
      </span>
    );

    if (label || description) {
      return (
        <label
          htmlFor={inputId}
          className={cn(
            "inline-flex gap-2.5 cursor-pointer select-none text-sm text-[#101828]",
            description ? "items-start" : "items-center",
            disabled && "cursor-not-allowed opacity-60",
          )}
        >
          <span className={cn("inline-flex", description && "pt-0.5")}>
            {checkboxElement}
          </span>
          <span className="flex flex-col">
            <span className="font-medium leading-none text-[#344054]">
              {label}
            </span>
            {description && (
              <span className="text-xs text-[#667085] mt-1">{description}</span>
            )}
          </span>
        </label>
      );
    }

    return (
      <label
        htmlFor={inputId}
        className={cn(
          "inline-flex cursor-pointer",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        {checkboxElement}
      </label>
    );
  },
);

Checkbox.displayName = "Checkbox";
